import { ref, Ref, unref, computed } from 'vue'
import loginService from '@/services/login.service'
import { User, UserManager, WebStorageStateStore, Log } from 'oidc-client-ts'

const SIGNIN_CALLBACK_PATH = '/auth/callback.html'
const SIGNIN_CALLBACK_SILENT_PATH = '/auth/silent-renew.html'
const HEMISPHERE_SESSION_STORAGE_KEY = 'hemisphere-session'
const SIGNIN_REDIRECT_URI = 'signin-redirect-uri'
const CONFIG = {
  userStore: new WebStorageStateStore({
    prefix: 'ubiant-oidc',
    store: window.localStorage
  }),
  client_id: 'platfom-account-dev',
  authority: 'https://sso.ubiant.com/realms/ubiant',
  redirect_uri: window.location.origin + SIGNIN_CALLBACK_PATH,
  response_type: 'code',
  scope: 'openid profile email hemisphere',
  post_logout_redirect_uri: window.location.origin + '/login',
  silent_redirect_uri: window.location.origin + SIGNIN_CALLBACK_SILENT_PATH,
  automaticSilentRenew: true,
  filterProtocolClaims: true,
  loadUserInfo: true
}

// toggle this flag for sites that allow use to access the site witout logging in.
// Loggin can then be perofrmed with
const ALLOW_OFFLINE = false

Log.setLogger(console)
// Log.setLevel(Log.DEBUG) // uncomment this to debug
Log.setLevel(Log.NONE)

interface SessionState {
  ssoSession: Ref<User | null>
  loggedIn: Ref<boolean>
  user: Ref<any>
  hemisphereToken: string | null
}

// reactive data to share state information with the app
const sessionState: SessionState = {
  ssoSession: ref(null),
  loggedIn: ref(false),
  user: ref(null),
  hemisphereToken: null
}

const mgr = new UserManager(CONFIG)
// mgr.events.addUserSignedIn(() => {
//   console.log('user signed_in')
// })
// mgr.events.addUserSessionChanged(() => {
//   console.log('UserSessionChanged')
// })
// mgr.events.addSilentRenewError(() => {
//   console.log('addSilentRenewError')
// })

// mgr.events.addUserLoaded((user) => {
//   console.log('addUserLoaded', user)
// })

// mgr.events.addUserSignedOut(() => {
//   console.log('addUserSignedOut')
// })

// mgr.events.addUserUnloaded(() => {
//   console.log('addUserUnloaded')
// })

mgr.events.addAccessTokenExpired(() => {
  //console.log('addAccessTokenExpired')
  setUser(null) // mark as logout
})

function loadUserFromStorage(): any {
  let user
  let token
  try {
    const sessionRaw = localStorage.getItem(HEMISPHERE_SESSION_STORAGE_KEY)
    if (sessionRaw !== 'undefined' && sessionRaw != null) {
      user = JSON.parse(sessionRaw)
      token = user.token
    }
  } catch (error) {
    console.log(error)
  }
  return { user, token }
}

function saveUserToStorage(user: any) {
  if (!user) {
    localStorage.removeItem(HEMISPHERE_SESSION_STORAGE_KEY)
    return
  }
  if (!user.token) {
    user.token = sessionState.hemisphereToken
  }
  localStorage.setItem(HEMISPHERE_SESSION_STORAGE_KEY, JSON.stringify(user))
}

async function ensureLoggedOrRedirect(to: string) {
  const ssoSession = unref(sessionState.ssoSession)
  // if no user found, redirect to keycloak
  if (!ssoSession || ssoSession.expired) {
    localStorage.setItem(SIGNIN_REDIRECT_URI, to)
    await mgr.signinRedirect()
    return // the line above will trigger redirect
  }

  await onSSOSessionUpdate(ssoSession)
}

async function logginPopup() {
  const ssoSession = await mgr.signinPopup()
  sessionState.ssoSession.value = ssoSession
  await onSSOSessionUpdate(ssoSession)
}

//
async function onSSOSessionUpdate(ssoSession: User) {
  let user = unref(sessionState.user)
  let token = sessionState.hemisphereToken

  if (!token || !user) {
    const { storageUser, storageToken } = loadUserFromStorage()
    user = storageUser
    token = storageToken
  }

  if (user && token && user.id == ssoSession.profile.sub) {
    sessionState.user.value = user
    sessionState.hemisphereToken = token
    sessionState.loggedIn.value = true
    return // valid session
  }

  await refreshHemisphereToken()
}

function logout() {
  // clear session state
  sessionState.hemisphereToken = null
  sessionState.user.value = null
  sessionState.loggedIn.value = false
  sessionState.ssoSession.value = null
  // clear localStorage
  localStorage.removeItem(HEMISPHERE_SESSION_STORAGE_KEY)
  // redirect
  mgr.signoutRedirect()
}

function setUser(user: any) {
  if (user?.token) {
    sessionState.hemisphereToken = user.token
  }
  sessionState.user.value = user
  saveUserToStorage(user)
}

export function getHemisphereToken() {
  return sessionState.hemisphereToken
}

export async function refreshHemisphereToken() {
  const ssoSession = sessionState.ssoSession.value
  if (ssoSession) {
    try {
      const res = await loginService.login2(ssoSession.access_token)
      if (res.data) {
        const user = res.data
        sessionState.user.value = user
        sessionState.hemisphereToken = user.token
        sessionState.loggedIn.value = true
      }
    } catch (error) {
      console.log(error)
    }
  } else {
    sessionState.user.value = null
    sessionState.hemisphereToken = null
    sessionState.loggedIn.value = false
  }
}

export async function initOidc() {
  //handle redirect
  if (window.location.pathname == SIGNIN_CALLBACK_PATH) {
    await mgr.signinCallback()
    // clear url to remove informations passed by keycloak
    const redirect_uri = localStorage.getItem(SIGNIN_REDIRECT_URI)
    console.log('redirect to', redirect_uri)
    ;(<any>window).location = window.location.origin + redirect_uri
    return // the line above will trigger redirect
  }

  if (window.location.pathname.startsWith(SIGNIN_CALLBACK_SILENT_PATH)) {
    await mgr.signinSilentCallback()
    ;(<any>window).location = window.location.origin
    return
  }

  // retrieve user from local storage
  let ssoSession
  try {
    ssoSession = await mgr.getUser()
    sessionState.ssoSession.value = ssoSession
  } catch (error) {
    console.log(error)
  }

  ssoSession = unref(sessionState.ssoSession)
  // if no user found, redirect to keycloak
  if (ssoSession && !ssoSession.expired) {
    await onSSOSessionUpdate(ssoSession)
  }
}

// composable export
export function useSession() {
  return {
    user: computed(() => sessionState.user.value || {}), // wrapper to return empty object to prevent nulls in renders
    loggedIn: sessionState.loggedIn,
    logout,
    setUser,
    logginPopup,
    ensureLoggedOrRedirect,
    refreshHemisphereToken
  }
}